import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { membershipApplicationAPI } from '../../services/api';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import Input from '../../components/UI/Input';
import Modal from '../../components/UI/Modal';
import { Trash2, Save, Edit, Clock } from 'lucide-react';
import { toast } from 'react-toastify';

const DISABILITY_OPTIONS = [
  'Mobility impairment',
  'Visual impairment',
  'Hearing impairment',
  'Speech impairment',
  'Intellectual or learning disability',
  'Psychosocial disability',
  'Chronic illness',
  'Other (please specify)',
];

const TRAINING_OPTIONS = [
  'Human Rights Advocacy',
  'Gender Equality & Women\'s Rights',
  'Legal Aid & Access to Justice',
  'Community Mobilization',
  'Leadership & Management',
  'Research & Documentation',
  'Monitoring & Evaluation',
  'Digital Security',
  'Financial Management',
  'Project Management',
  'Communication & Media',
  'Psychosocial Support',
  'Other',
];

const ETHIOPIAN_REGIONS = [
  'Addis Ababa',
  'Afar',
  'Amhara',
  'Benishangul-Gumuz',
  'Central Ethiopia',
  'Dire Dawa',
  'Gambela',
  'Harari',
  'Oromia',
  'Sidama',
  'Somali',
  'South West Ethiopia',
  'Southern Nations, Nationalities, and Peoples',
  'Tigray',
];

const REGION_FIELD_NAMES = new Set([
  'region',
  'region_city',
  'region_city_town',
  'region_city_zone',
  'region_city_woreda',
]);

const withCoreFieldDefaults = (field) => {
  if (!field) return field;

  const next = { ...field };

  if (REGION_FIELD_NAMES.has(next.field_name)) {
    next.field_type = 'select';
    next.options = ETHIOPIAN_REGIONS;
    if (!next.placeholder) {
      next.placeholder = 'Select your region';
    }
  }

  return next;
};

const Container = styled.div`
  padding: 2rem;
  background-color: ${props => props.theme.colors.background.primary};
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled(Card)`
  padding: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 1.5rem 0;
`;

const FieldTypes = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.75rem;
  margin-bottom: 2rem;
`;

const FieldTypeButton = styled.button`
  padding: 0.75rem;
  border: 2px dashed ${props => props.theme.colors.border.light};
  border-radius: 8px;
  background: none;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.primary}10;
  }
`;

const FieldsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FieldItem = styled.div`
  position: relative;
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: 8px;
  padding: 1rem;
  background: ${props => props.theme.colors.white};
  cursor: grab;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  user-select: none;

  ${props => props.$isDragging && `
    border-color: ${props.theme.colors.primary};
    box-shadow: 0 6px 14px ${props.theme.colors.primary}26;
    opacity: 0.95;
  `}

  ${props => props.$isDragOver && `
    box-shadow: inset 0 0 0 2px ${props.theme.colors.primary}66;
    background: ${props.theme.colors.primary}08;
  `}
`;

const DropZone = styled.div`
  height: 48px;
  border: 2px dashed transparent;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.text.secondary};
  font-size: 0.85rem;
  margin-top: 0.5rem;
  opacity: ${props => (props.$isActive ? 1 : 0.4)};
  transition: border-color 0.2s ease, background 0.2s ease, opacity 0.2s ease;

  ${props => props.$isActive && `
    border-color: ${props.theme.colors.primary};
    background: ${props.theme.colors.primary}12;
    color: ${props.theme.colors.primary};
  `}
`;

const FieldHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const FieldLabel = styled.span`
  font-weight: 500;
  color: ${props => props.theme.colors.text.primary};
`;

const FieldActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const DateRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const MembershipApplicationFormBuilder = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'public',
    is_active: false,
    opens_at: '',
    closes_at: '',
    instructions: '',
    closed_message: 'The membership application period is currently closed.',
  });

  const [fields, setFields] = useState([]);
  const [editingField, setEditingField] = useState(null);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dragSourceIndex, setDragSourceIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const ensureAccessibilityFields = useCallback((incomingFields = [], { notify = false, formType = 'public' } = {}) => {
    const normalized = Array.isArray(incomingFields) ? [...incomingFields] : [];
    const existingNames = new Set(normalized.map(field => field.field_name));

    const highestOrder = normalized.length
      ? Math.max(
          ...normalized.map((field, index) =>
            typeof field.display_order === 'number' ? field.display_order : index
          )
        )
      : -1;

    let nextOrder = highestOrder + 1;
    let added = false;

    const appendField = (definition) => {
      if (existingNames.has(definition.field_name)) {
        return;
      }

      normalized.push({
        ...definition,
        display_order: nextOrder++,
      });
      existingNames.add(definition.field_name);
      added = true;
    };

    if (formType === 'registration') {
      appendField({
        field_name: 'has_disability',
        field_label: 'Do you have a physical disability?',
        field_type: 'radio',
        options: ['Yes', 'No'],
        is_required: false,
        help_text: 'Indicate whether the applicant reports a physical disability.',
        is_active: true,
      });

      appendField({
        field_name: 'disability_type',
        field_label: 'Disability Type',
        field_type: 'select',
        options: DISABILITY_OPTIONS,
        is_required: false,
        help_text: 'If disability is selected, choose the most relevant description.',
        is_active: true,
      });

      appendField({
        field_name: 'cv_file',
        field_label: 'Upload CV / Resume',
        field_type: 'file',
        is_required: false,
        help_text: 'Optional. Accepted formats: PDF, DOC, DOCX (max 5 MB).',
        is_active: true,
      });
    } else {
      appendField({
        field_name: 'has_disability',
        field_label: 'Do you identify as having a physical disability?',
        field_type: 'radio',
        options: ['Yes', 'No'],
        is_required: false,
        help_text: 'This information helps us plan for accessibility needs during engagements.',
        is_active: true,
      });

      appendField({
        field_name: 'disability_types',
        field_label: 'Disability Type(s)',
        field_type: 'checkbox',
        options: DISABILITY_OPTIONS,
        is_required: false,
        help_text: 'Select all that apply. Choose “Other (please specify)” if none of the categories fit.',
        is_active: true,
      });

      appendField({
        field_name: 'disability_other',
        field_label: 'Disability Details',
        field_type: 'text',
        is_required: false,
        help_text: 'If you selected “Other”, please describe your accessibility needs here.',
        placeholder: 'Describe accessibility needs',
        is_active: true,
      });

      appendField({
        field_name: 'cv',
        field_label: 'Upload CV / Resume',
        field_type: 'file',
        is_required: false,
        help_text: 'Optional. Accepted formats: PDF, DOC, DOCX (max 5 MB).',
        is_active: true,
      });

      // New Membership Fields - ID Card
      appendField({
        field_name: 'id_card',
        field_label: 'Upload ID Card',
        field_type: 'file',
        is_required: true,
        help_text: 'Required. Upload a copy of your ID card. Accepted formats: PDF, JPG, PNG (max 5 MB).',
        is_active: true,
      });

      // New Membership Fields - Interested Trainings
      appendField({
        field_name: 'interested_trainings',
        field_label: 'Interested Training(s)',
        field_type: 'checkbox',
        options: TRAINING_OPTIONS,
        is_required: false,
        help_text: 'Select all training programs you are interested in joining.',
        is_active: true,
      });

      // New Membership Fields - Previous Training
      appendField({
        field_name: 'has_previous_training',
        field_label: 'Have you previously taken any training?',
        field_type: 'radio',
        options: ['Yes', 'No'],
        is_required: false,
        help_text: 'If yes, you can add details about your previous trainings in the application form.',
        is_active: true,
      });
    }

    if (added && notify) {
      toast.success('Accessibility, training & supporting document fields have been added to the form.');
    }

    return { fields: normalized.map(withCoreFieldDefaults), added };
  }, []);

  const fieldTypes = [
    { type: 'text', label: 'Text' },
    { type: 'textarea', label: 'Textarea' },
    { type: 'email', label: 'Email' },
    { type: 'tel', label: 'Phone' },
    { type: 'number', label: 'Number' },
    { type: 'date', label: 'Date' },
    { type: 'select', label: 'Select' },
    { type: 'radio', label: 'Radio' },
    { type: 'checkbox', label: 'Checkbox' },
    { type: 'file', label: 'File' },
  ];

  const loadForm = useCallback(async () => {
    try {
      const res = await membershipApplicationAPI.getForm(id);
      const form = res.data;
      setFormData({
        title: form.title || '',
        description: form.description || '',
        type: form.type || 'public',
        is_active: form.is_active || false,
        opens_at: form.opens_at ? new Date(form.opens_at).toISOString().slice(0, 16) : '',
        closes_at: form.closes_at ? new Date(form.closes_at).toISOString().slice(0, 16) : '',
        instructions: form.instructions || '',
        closed_message: form.closed_message || '',
      });
      const incomingFields =
        form.fields ||
        form.active_fields ||
        form.activeFields ||
        [];
      const { fields: ensuredFields, added } = ensureAccessibilityFields(incomingFields, { formType: form.type || 'public' });
      setNormalizedFields(ensuredFields);
      if (added) {
        toast.info('Accessibility fields have been added to this form for consistency.');
      }

    } catch (error) {
      toast.error('Failed to load form');
    }
  }, [id, ensureAccessibilityFields]);

  useEffect(() => {
    if (isEdit) {
      loadForm();
    }
  }, [isEdit, loadForm]);

  const handleAddField = (fieldType) => {
    const newField = {
      field_name: `field_${Date.now()}`,
      field_label: 'New Field',
      field_type: fieldType,
      placeholder: '',
      help_text: '',
      is_required: false,
      options: fieldType === 'select' || fieldType === 'radio' ? ['Option 1', 'Option 2'] : null,
      display_order: fields.length,
      is_active: true,
    };
    setEditingField(newField);
    setShowFieldModal(true);
  };

  const handleEditField = (field, index) => {
    setEditingField({ ...field, _index: index });
    setShowFieldModal(true);
  };

  const handleSaveField = () => {
    if (!editingField) return;

    // Validate required fields
    if (!editingField.field_label || !editingField.field_label.trim()) {
      toast.error('Please enter a field label');
      return;
    }

    if (!editingField.field_name || !editingField.field_name.trim()) {
      toast.error('Please enter a field name');
      return;
    }

    // Validate field name format (should be lowercase, underscores, no spaces)
    const fieldNameRegex = /^[a-z][a-z0-9_]*$/;
    if (!fieldNameRegex.test(editingField.field_name.trim())) {
      toast.error('Field name must start with a letter and contain only lowercase letters, numbers, and underscores');
      return;
    }

    // Check for duplicate field names
    const existingFieldIndex = fields.findIndex(
      (f, idx) => f.field_name === editingField.field_name.trim() && idx !== editingField._index
    );
    if (existingFieldIndex !== -1) {
      toast.error('A field with this name already exists');
      return;
    }

    const normalizeRules = (rules) => {
      if (!rules || (Array.isArray(rules) && rules.length === 0)) {
        return null;
      }
      if (Array.isArray(rules)) {
        const cleaned = rules
          .map(rule => (rule === null ? null : rule.toString().trim()))
          .filter(rule => rule);
        return cleaned.length ? cleaned : null;
      }
      if (typeof rules === 'string') {
        const cleaned = rules
          .split('|')
          .map(rule => rule.trim())
          .filter(rule => rule);
        return cleaned.length ? cleaned : null;
      }
      return null;
    };

    const fieldData = { 
      ...editingField,
      field_name: editingField.field_name.trim(),
      field_label: editingField.field_label.trim(),
      placeholder: editingField.placeholder?.trim() || null,
      help_text: editingField.help_text?.trim() || null,
      validation_rules: normalizeRules(editingField.validation_rules),
    };
    delete fieldData._index;

    // Ensure options is properly formatted for select/radio/checkbox
    if ((fieldData.field_type === 'select' || fieldData.field_type === 'radio' || fieldData.field_type === 'checkbox')) {
      if (Array.isArray(fieldData.options) && fieldData.options.length > 0) {
        fieldData.options = fieldData.options.filter(opt => opt && opt.trim());
      } else {
        fieldData.options = null;
      }
    } else {
      fieldData.options = null;
    }

    const normalizedFieldData = withCoreFieldDefaults(fieldData);

    if (editingField._index !== undefined) {
      setNormalizedFields(prev => {
        const updated = [...prev];
        updated[editingField._index] = normalizedFieldData;
        return updated;
      });
      toast.success('Field updated successfully');
    } else {
    setNormalizedFields(prev => [...prev, withCoreFieldDefaults(normalizedFieldData)]);
      toast.success('Field added successfully');
    }

    setShowFieldModal(false);
    setEditingField(null);
  };

  const handleDeleteField = (index) => {
    setNormalizedFields(prev => prev.filter((_, i) => i !== index));
  };

  const sortedFields = useMemo(() => {
    return fields
      .map((field, index) => ({
        field: withCoreFieldDefaults(field),
        index,
      }))
      .sort((a, b) => {
        const orderA = a.field.display_order ?? a.index;
        const orderB = b.field.display_order ?? b.index;
        return orderA - orderB;
      });
  }, [fields]);

  const reorderFieldsWithDisplayOrder = (list = []) =>
    list.map((field, idx) => ({
      ...withCoreFieldDefaults(field),
      display_order: idx,
    }));

  const setNormalizedFields = (updater) => {
    if (typeof updater === 'function') {
      setFields(prev => reorderFieldsWithDisplayOrder(updater(prev)));
    } else {
      setFields(reorderFieldsWithDisplayOrder(updater));
    }
  };

  const handleReorderFields = (sourceIndex, destinationIndex) => {
    if (
      sourceIndex === destinationIndex ||
      sourceIndex === null ||
      destinationIndex === null ||
      sourceIndex < 0
    ) {
      return;
    }

    setNormalizedFields(prevFields => {
      if (!prevFields || prevFields.length === 0) {
        return prevFields;
      }

      const clampedDestination = Math.max(0, Math.min(destinationIndex, prevFields.length));
      const updated = [...prevFields];
      const [moved] = updated.splice(sourceIndex, 1);

      const insertIndex = clampedDestination >= updated.length
        ? updated.length
        : clampedDestination;

      updated.splice(insertIndex, 0, moved);
      return updated;
    });
  };

  const handleMoveField = (index, direction) => {
    setNormalizedFields(prev => {
      if (direction === 'up' && index === 0) return prev;
      if (direction === 'down' && index === prev.length - 1) return prev;
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      const updated = [...prev];
      [updated[index], updated[targetIndex]] = [updated[targetIndex], updated[index]];
      return updated;
    });
  };

  const handleDragStart = (event, sourceIndex) => {
    setDragSourceIndex(sourceIndex);
    setDragOverIndex(sourceIndex);
    if (event.dataTransfer?.setData) {
      try {
        event.dataTransfer.setData('text/plain', String(sourceIndex));
      } catch (error) {
        // ignore
      }
    }
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
  };

  const handleDragOver = (event, destinationIndex) => {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    if (dragOverIndex !== destinationIndex) {
      setDragOverIndex(destinationIndex);
    }
  };

  const handleDragLeave = (event, destinationIndex) => {
    const related = event.relatedTarget;
    if (event.currentTarget && related && event.currentTarget.contains(related)) {
      return;
    }
    if (dragOverIndex === destinationIndex) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (event, destinationIndex) => {
    event.preventDefault();
    if (dragSourceIndex !== null) {
      handleReorderFields(dragSourceIndex, destinationIndex);
    }
    setDragSourceIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragSourceIndex(null);
    setDragOverIndex(null);
  };

  const handleSave = async () => {
    if (!formData.title) {
      toast.error('Please enter a form title');
      return;
    }

    // Validate all fields before saving
    const invalidFields = fields.filter(field => 
      !field.field_label || !field.field_label.trim() || 
      !field.field_name || !field.field_name.trim()
    );

    if (invalidFields.length > 0) {
      toast.error('Please complete all field labels and field names before saving');
      return;
    }

    // Check for duplicate field names
    const fieldNames = fields.map(f => f.field_name?.trim()).filter(Boolean);
    const duplicates = fieldNames.filter((name, index) => fieldNames.indexOf(name) !== index);
    if (duplicates.length > 0) {
      toast.error(`Duplicate field names found: ${[...new Set(duplicates)].join(', ')}`);
      return;
    }

    setLoading(true);
    try {
      const { fields: ensuredFields } = ensureAccessibilityFields(fields, { formType: formData.type });
      if (ensuredFields !== fields) {
        setNormalizedFields(ensuredFields);
      }

      const submitData = {
        ...formData,
        fields: ensuredFields.map((field, index) => {
          const normalizeOptions = (options) => {
            if (!options) return null;
            if (!Array.isArray(options)) return null;
            const cleaned = options
              .map(opt => (opt === null ? null : opt.toString().trim()))
              .filter(opt => opt);
            return cleaned.length ? cleaned : null;
          };

          const normalizeRules = (rules) => {
            if (!rules || (Array.isArray(rules) && rules.length === 0)) {
              return null;
            }
            if (Array.isArray(rules)) {
              const cleaned = rules
                .map(rule => (rule === null ? null : rule.toString().trim()))
                .filter(rule => rule);
              return cleaned.length ? cleaned : null;
            }
            if (typeof rules === 'string') {
              const cleaned = rules
                .split('|')
                .map(rule => rule.trim())
                .filter(rule => rule);
              return cleaned.length ? cleaned : null;
            }
            return null;
          };

          return {
            field_name: field.field_name?.trim(),
            field_label: field.field_label?.trim(),
            field_type: field.field_type,
            placeholder: field.placeholder?.trim() || null,
            help_text: field.help_text?.trim() || null,
            is_required: field.is_required || false,
            options: normalizeOptions(field.options),
            validation_rules: normalizeRules(field.validation_rules),
            display_order: index,
            is_active: field.is_active !== false, // Default to true
          };
        }),
      };

      console.log('Saving form with fields:', submitData.fields);

      if (isEdit) {
        await membershipApplicationAPI.updateForm(id, submitData);
        toast.success('Form updated successfully');
      } else {
        await membershipApplicationAPI.createForm(submitData);
        toast.success('Form created successfully');
      }
      
      navigate('/app/membership-applications/forms');
    } catch (error) {
      console.error('Error saving form:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.errors || 'Failed to save form';
      toast.error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container theme={theme}>
      <Header theme={theme}>
        <Title theme={theme}>
          {isEdit ? 'Edit Membership Form' : 'Create Membership Form'}
        </Title>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <Button variant="outline" onClick={() => navigate('/app/membership-applications/forms')}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading} leftIcon={<Save size={16} />}>
            {loading ? 'Saving...' : 'Save Form'}
          </Button>
        </div>
      </Header>

      <FormGrid>
        <Panel theme={theme}>
          <SectionTitle theme={theme}>Form Settings</SectionTitle>

          <div style={{ marginBottom: '1rem' }}>
            <Input
              label="Form Title *"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Membership Application 2025"
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Form Type *
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: `1px solid ${theme.colors.border.medium}`,
                borderRadius: theme.borderRadius.md,
                fontSize: theme.typography.fontSize.sm,
                backgroundColor: theme.colors.white,
                color: theme.colors.text.primary,
              }}
            >
              <option value="public">Public Application</option>
              <option value="recommendation">Recommendation</option>
              <option value="registration">Registration</option>
            </select>
            <p style={{ fontSize: '0.875rem', color: theme.colors.text.secondary, marginTop: '0.5rem' }}>
              Select the type of form. Registration forms are used for approved applicants to complete their membership details.
            </p>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <Input
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Form description..."
              type="textarea"
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <Input
              label="Instructions"
              value={formData.instructions}
              onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
              placeholder="Instructions for applicants..."
              type="textarea"
            />
          </div>

          <SectionTitle theme={theme} style={{ marginTop: '2rem' }}>
            <Clock size={18} style={{ display: 'inline', marginRight: '0.5rem' }} />
            Application Period
          </SectionTitle>

          <DateRow>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Opens At
              </label>
              <Input
                type="datetime-local"
                value={formData.opens_at}
                onChange={(e) => setFormData({ ...formData, opens_at: e.target.value })}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Closes At
              </label>
              <Input
                type="datetime-local"
                value={formData.closes_at}
                onChange={(e) => setFormData({ ...formData, closes_at: e.target.value })}
              />
            </div>
          </DateRow>

          <div style={{ marginBottom: '1rem' }}>
            <Input
              label="Closed Message"
              value={formData.closed_message}
              onChange={(e) => setFormData({ ...formData, closed_message: e.target.value })}
              placeholder="Message to show when application is closed"
              type="textarea"
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              <span>Activate this form</span>
            </label>
          </div>
        </Panel>

        <Panel theme={theme}>
          <SectionTitle theme={theme}>Form Fields</SectionTitle>

          <FieldTypes>
            {fieldTypes.map((ft) => (
              <FieldTypeButton
                key={ft.type}
                onClick={() => handleAddField(ft.type)}
                theme={theme}
              >
                {ft.label}
              </FieldTypeButton>
            ))}
          </FieldTypes>

          <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: '500', color: theme.colors.text.primary }}>
              Fields ({fields.length})
            </span>
            {fields.length > 0 && (
              <span style={{ fontSize: '0.875rem', color: theme.colors.text.secondary }}>
                Drag to reorder or use ↑↓ buttons
              </span>
            )}
          </div>

          <FieldsList>
            {sortedFields.map(({ field, index }, displayIndex) => {
              const isDragging = dragSourceIndex === index;
              const isDragOver = dragOverIndex === index && dragSourceIndex !== index;
              return (
                <FieldItem
                  key={`${field.field_name || 'field'}-${index}`}
                  theme={theme}
                  draggable
                  $isDragging={isDragging}
                  $isDragOver={isDragOver}
                  onDragStart={(event) => handleDragStart(event, index)}
                  onDragOver={(event) => handleDragOver(event, index)}
                  onDragEnter={(event) => handleDragOver(event, index)}
                  onDragLeave={(event) => handleDragLeave(event, index)}
                  onDrop={(event) => handleDrop(event, index)}
                  onDragEnd={handleDragEnd}
                >
                  <FieldHeader>
                    <div style={{ flex: 1 }}>
                      <FieldLabel theme={theme}>
                        <span style={{ fontWeight: '600' }}>{displayIndex + 1}.</span> {field.field_label || 'Unnamed Field'}
                        <span style={{ fontSize: '0.875rem', color: theme.colors.text.secondary, marginLeft: '0.5rem' }}>
                          ({field.field_type})
                        </span>
                        {field.is_required && <span style={{ color: 'red', marginLeft: '0.25rem' }}> *</span>}
                      </FieldLabel>
                      {field.field_name && (
                        <div style={{ fontSize: '0.75rem', color: theme.colors.text.secondary, marginTop: '0.25rem' }}>
                          Field name: <code style={{ background: theme.colors.background.secondary, padding: '0.125rem 0.25rem', borderRadius: '4px' }}>{field.field_name}</code>
                        </div>
                      )}
                    </div>
                    <FieldActions>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleMoveField(index, 'up')}
                        disabled={index === 0}
                        title="Move up"
                      >
                        ↑
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleMoveField(index, 'down')}
                        disabled={index === fields.length - 1}
                        title="Move down"
                      >
                        ↓
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditField(field, index)}
                        leftIcon={<Edit size={14} />}
                        title="Edit field"
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          if (window.confirm('Are you sure you want to delete this field?')) {
                            handleDeleteField(index);
                          }
                        }}
                        leftIcon={<Trash2 size={14} />}
                        title="Delete field"
                      >
                        Delete
                      </Button>
                    </FieldActions>
                  </FieldHeader>
                </FieldItem>
              );
            })}
            <DropZone
              onDragOver={(event) => handleDragOver(event, fields.length)}
              onDragEnter={(event) => handleDragOver(event, fields.length)}
              onDragLeave={(event) => handleDragLeave(event, fields.length)}
              onDrop={(event) => handleDrop(event, fields.length)}
              onDragEnd={handleDragEnd}
              $isActive={dragOverIndex === fields.length}
            >
              {dragSourceIndex === null ? 'Drag a field to reorder' : 'Release here to move field to the end'}
            </DropZone>
            {fields.length === 0 && (
              <div style={{ textAlign: 'center', padding: '2rem', color: theme.colors.text.secondary }}>
                <p>No fields added yet.</p>
                <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  Click a field type above to add a new field to your form.
                </p>
              </div>
            )}
          </FieldsList>
        </Panel>
      </FormGrid>

      {/* Field Edit Modal */}
      <Modal
        isOpen={showFieldModal}
        onClose={() => {
          setShowFieldModal(false);
          setEditingField(null);
        }}
        title="Edit Field"
      >
        {editingField && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <Input
              label="Field Label *"
              value={editingField.field_label || ''}
              onChange={(e) => setEditingField({ ...editingField, field_label: e.target.value })}
            />
            <div>
              <Input
                label="Field Name *"
                value={editingField.field_name || ''}
                onChange={(e) => {
                  // Auto-format: lowercase, replace spaces with underscores
                  const formatted = e.target.value
                    .toLowerCase()
                    .replace(/\s+/g, '_')
                    .replace(/[^a-z0-9_]/g, '');
                  setEditingField({ ...editingField, field_name: formatted });
                }}
                placeholder="e.g., full_name, email, phone_number"
              />
              <small style={{ color: theme.colors.text.secondary, fontSize: '0.75rem', display: 'block', marginTop: '0.25rem' }}>
                Field name must be lowercase letters, numbers, and underscores only (e.g., full_name)
              </small>
            </div>
            <Input
              label="Placeholder"
              value={editingField.placeholder || ''}
              onChange={(e) => setEditingField({ ...editingField, placeholder: e.target.value })}
            />
            <Input
              label="Help Text"
              value={editingField.help_text || ''}
              onChange={(e) => setEditingField({ ...editingField, help_text: e.target.value })}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={editingField.is_required || false}
                onChange={(e) => setEditingField({ ...editingField, is_required: e.target.checked })}
              />
              <span>Required field</span>
            </label>
            {(editingField.field_type === 'select' || editingField.field_type === 'radio' || editingField.field_type === 'checkbox') && (
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Options (one per line)
                </label>
                <textarea
                  value={Array.isArray(editingField.options) ? editingField.options.join('\n') : ''}
                  onChange={(e) => setEditingField({
                    ...editingField,
                    options: e.target.value.split('\n').filter(o => o.trim())
                  })}
                  rows={5}
                  style={{ width: '100%', padding: '0.5rem', border: `1px solid ${theme.colors.border.light}`, borderRadius: '8px' }}
                />
              </div>
            )}
            {editingField.field_type === 'file' && (
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Validation Rules
                </label>
                <Input
                  placeholder="e.g. mimes:pdf,doc,docx|max:5120"
                  value={Array.isArray(editingField.validation_rules) ? editingField.validation_rules.join('|') : (editingField.validation_rules || '')}
                  onChange={(e) => {
                    const value = e.target.value;
                    const rulesArray = value
                      .split('|')
                      .map(rule => rule.trim())
                      .filter(Boolean);
                    setEditingField({
                      ...editingField,
                      validation_rules: rulesArray.length ? rulesArray : null,
                    });
                  }}
                />
                <small style={{ color: theme.colors.text.secondary, fontSize: '0.75rem', display: 'block', marginTop: '0.25rem' }}>
                  Separate multiple rules with `|`, same as Laravel validation.
                </small>
              </div>
            )}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <Button variant="outline" onClick={() => {
                setShowFieldModal(false);
                setEditingField(null);
              }}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSaveField}>
                Save Field
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </Container>
  );
};

export default MembershipApplicationFormBuilder;


